/**
 * Live chat dictation for plans with `voice_transcription` (MAR-1423): the microphone, as 16 kHz PCM,
 * over a WebSocket to `/api/dictation/live`, which relays it through the LLM gateway to Gemini Live
 * (`gemini-3.5-transcribe-live`). Interim text arrives while the user speaks, final text on pauses.
 *
 * `start` resolves `'unavailable'` when the live path cannot be used — no microphone, no token, the
 * server answering `unavailable` (plan, gateway, quota, Google), or no answer in time. The composer
 * then falls back to the browser's own `SpeechRecognition` (`ChatInput.vue`). If the user clicks to
 * stop while `start` is still opening the mic or waiting for `ready`, `start` cleans up and resolves
 * `'cancelled'` so the composer returns to idle without falling back to browser dictation (MAR-1490).
 *
 * Turns: nothing is sent until the user speaks, and about a second of silence after speech sends
 * `{"type":"pause"}`, which closes the turn so Gemini finalises it (`TurnTracker`, `utils/dictation/pcm.ts`).
 *
 * Cost guards (Alex, 2026-10-28): a dictation stops itself after `SILENCE_STOP_MS` of silence and
 * after `DICTATION_MAX_MS`, and never reconnects. The gateway enforces the 10 minutes as well.
 */
import { getCurrentInstance, onScopeDispose } from 'vue'
import {
  DICTATION_MAX_MS,
  PcmChunker,
  SilenceWatch,
  TurnTracker,
  chunkLevel,
  downsampleToInt16,
} from '~/utils/dictation/pcm'

export type LiveDictationStartOutcome = 'started' | 'cancelled' | 'unavailable'

export interface LiveDictationHandlers {
  /** Every finished utterance so far, and the utterance in progress. */
  onText(finals: string[], interim: string): void
  /** Microphone capture has stopped; waiting briefly for any in-flight final transcript to drain. */
  onStopping?(): void
  /** The dictation is over: stopped, silent, out of time, or the connection ended. */
  onEnd(): void
}

/** How long the server has to say `ready` before the composer falls back. */
const READY_TIMEOUT_MS = 8_000
/** After `stop`, the fallback ceiling if an open turn's final transcript never arrives. */
const DRAIN_MS = 2_000

/** `public/dictation/pcm-tap-worklet.js`: posts each block of microphone samples to this thread. */
const WORKLET_URL = '/dictation/pcm-tap-worklet.js'

export function useLiveDictation() {
  let teardown: (() => void) | null = null
  let finish: (() => void) | null = null

  function supported(): boolean {
    return typeof window !== 'undefined'
      && typeof WebSocket !== 'undefined'
      && typeof AudioWorkletNode !== 'undefined'
      && Boolean(navigator.mediaDevices?.getUserMedia)
  }

  async function start(handlers: LiveDictationHandlers): Promise<LiveDictationStartOutcome> {
    if (!supported()) return 'unavailable'

    let cancelled = false
    let cancelPendingOpen: (() => void) | null = null
    const cancelDuringStart = () => {
      cancelled = true
      cancelPendingOpen?.()
    }
    finish = cancelDuringStart
    teardown = cancelDuringStart

    const clearControls = () => {
      if (finish === cancelDuringStart) finish = null
      if (teardown === cancelDuringStart) teardown = null
    }

    const { getToken } = useAuth()
    const token = await getToken()
    if (cancelled) {
      clearControls()
      return 'cancelled'
    }
    if (!token) {
      clearControls()
      return 'unavailable'
    }

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } })
    } catch {
      clearControls()
      return cancelled ? 'cancelled' : 'unavailable'
    }
    if (cancelled) {
      stream.getTracks().forEach(track => track.stop())
      clearControls()
      return 'cancelled'
    }

    const context = new AudioContext()
    const cleanupAudio = () => {
      stream.getTracks().forEach(track => track.stop())
      void context.close().catch(() => {})
    }
    let node: AudioWorkletNode
    try {
      await context.audioWorklet.addModule(WORKLET_URL)
      if (cancelled) {
        cleanupAudio()
        clearControls()
        return 'cancelled'
      }
      node = new AudioWorkletNode(context, 'marcus-pcm-tap')
      context.createMediaStreamSource(stream).connect(node)
    } catch {
      cleanupAudio()
      clearControls()
      return cancelled ? 'cancelled' : 'unavailable'
    }

    const socket = new WebSocket(`${location.origin.replace(/^http/, 'ws')}/api/dictation/live`)
    socket.binaryType = 'arraybuffer'

    const chunker = new PcmChunker()
    const silence = new SilenceWatch(Date.now())
    const turns = new TurnTracker()
    const finals: string[] = []
    let interim = ''
    let ready = false
    let stopping = false
    let ended = false
    let capTimer: ReturnType<typeof setTimeout> | null = null
    let drainTimer: ReturnType<typeof setTimeout> | null = null

    const end = () => {
      if (ended) return
      ended = true
      if (capTimer) clearTimeout(capTimer)
      if (drainTimer) clearTimeout(drainTimer)
      node.port.onmessage = null
      cleanupAudio()
      try { socket.close() } catch { /* already closed */ }
      teardown = null
      finish = null
      if (ready) {
        // Whatever was still interim when the dictation ended stays in the composer.
        if (interim) handlers.onText([...finals, interim], '')
        handlers.onEnd()
      }
    }

    /** Stop listening, let an open turn's last transcript arrive (if any), then close. */
    const stopListening = () => {
      if (ended || stopping) return
      stopping = true
      if (capTimer) {
        clearTimeout(capTimer)
        capTimer = null
      }
      node.port.onmessage = null
      stream.getTracks().forEach(track => track.stop())
      const awaitingTurn = turns.inTurn || interim.length > 0
      if (ready && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'end' }))
      }
      handlers.onStopping?.()
      if (ready && socket.readyState === WebSocket.OPEN && awaitingTurn) {
        drainTimer = setTimeout(end, DRAIN_MS)
      } else {
        end()
      }
    }

    node.port.onmessage = (event: MessageEvent<Float32Array>) => {
      if (!ready || stopping || ended) return
      const now = Date.now()
      for (const chunk of chunker.push(downsampleToInt16(event.data, context.sampleRate))) {
        const level = chunkLevel(chunk)
        const { send, pause } = turns.push(chunk, level, now)
        for (const frame of send) socket.send(frame)
        if (pause) socket.send(JSON.stringify({ type: 'pause' }))
        if (silence.observe(level, now)) {
          stopListening()
          return
        }
      }
    }

    const opened = await new Promise<LiveDictationStartOutcome>((resolve) => {
      let settled = false
      const settle = (outcome: LiveDictationStartOutcome) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        cancelPendingOpen = null
        resolve(outcome)
      }
      const timer = setTimeout(() => settle('unavailable'), READY_TIMEOUT_MS)
      cancelPendingOpen = () => settle('cancelled')
      if (cancelled) {
        settle('cancelled')
        return
      }
      socket.onopen = () => {
        if (!cancelled) socket.send(JSON.stringify({ type: 'start', token }))
      }
      socket.onmessage = (event: MessageEvent) => {
        if (typeof event.data !== 'string') return
        let message: { type?: string, text?: string }
        try { message = JSON.parse(event.data) } catch { return }
        if (!ready) {
          settle(message.type === 'ready' ? 'started' : 'unavailable')
          return
        }
        if (message.type === 'interim') {
          interim = message.text ?? ''
          silence.heard(Date.now())
          handlers.onText([...finals], interim)
        } else if (message.type === 'final') {
          if (message.text) finals.push(message.text)
          interim = ''
          silence.heard(Date.now())
          handlers.onText([...finals], interim)
          if (stopping) end()
        } else if (message.type === 'closed') {
          end()
        }
      }
      socket.onclose = () => {
        if (ready) end()
        else settle(cancelled ? 'cancelled' : 'unavailable')
      }
      socket.onerror = () => { /* the close that follows decides */ }
    })

    if (opened !== 'started') {
      ended = true
      node.port.onmessage = null
      cleanupAudio()
      try { socket.close() } catch { /* already closed */ }
      teardown = null
      finish = null
      return opened
    }

    ready = true
    silence.heard(Date.now())
    capTimer = setTimeout(stopListening, DICTATION_MAX_MS)
    teardown = end
    finish = stopListening
    return 'started'
  }

  /** The user's stop: the last words still arrive before the dictation ends. */
  function stop(): void {
    finish?.()
  }

  if (getCurrentInstance()) onScopeDispose(() => teardown?.())

  return { start, stop, supported }
}
